"use client";

import Image from "next/image";
import { getTranslation } from "@/i18n/get-translation";
import {
  IDoctorAppointmetApproved
} from "@/types/agenda.type";
import MOMENTJS_DATE_FORMATES from "@/common/constants/momentjs-format";
import { useEffect, useState } from "react";
import {FaCalendarDay, FaClock, FaLocationDot} from 'react-icons/fa6'
import dayjs from "dayjs";
import { scrollToElement } from "@/utils/utils";

export default function DoctorAppointmentApproved({
  locale,
  doctorInfo,
  selectedDateTime,
  selectedReason,
}: IDoctorAppointmetApproved) {
  const t = getTranslation(locale, "appointment");
  const [profileImage, setProfileImage] = useState<string>("/images/doctor-male-avatar.webp");  

  const doctorAddress: string[] | undefined = doctorInfo?.docInf?.address?.split(';');
  const doctorDesignation: string[] | undefined = doctorInfo?.docInf?.speciality?.split(';');

  useEffect(()=>{
    if(doctorInfo?.docInf?.media?.url) {
      setProfileImage(doctorInfo?.docInf?.media?.url);
    }
    else {
      const placeholderImage = doctorInfo?.docInf?.gender === 'male' ? '/images/doctor-male-avatar.webp' : '/images/doctor-female-avatar.webp';
      setProfileImage(placeholderImage);
    }
  }, [doctorInfo]);

  const isAutoConfirm = doctorInfo?.auto_confirm_online_appointment ?? true;

  return (
    <section className="relative">
      <p className="text-agenda-purple-800 mt-1 mb-4 leading-relaxed">
        {isAutoConfirm ? (
          <span>{t("scheduled_successfully")}</span>
        ) : (
          <span>{t("pending_approval")}</span>
        )}
      </p>
      <div className="w-full flex items-center">
        <figure className="bg-white bg-linear-to-t from-[#e5f5ff] to-white rounded-full w-17.5 h-17.5 md:w-20 md:h-20 flex-none border border-gray-200 mb-1 overflow-hidden mr-3 md:mr-4">
          <Image
            priority={true}
            width={150}
            height={150}
            src={profileImage}
            alt=""
            className="w-full h-full object-cover"
          />
        </figure>
        <div className="">
          <h1 className="text-agenda-purple-800 text-base md:text-xl font-bold">
            {doctorInfo?.docInf?.name}
          </h1>
          <h2 className="text-agenda-blue-600 font-medium mt-1 text-sm md:text-base">
            {doctorDesignation &&
              doctorDesignation.map((designationItem: string, index: number) => {
                return <div key={`designation_${index}`}>{designationItem}</div>;
              })}
          </h2>
        </div>
      </div>
      <div className="text-sm md:text-base">
        <address className="text-agenda-purple-800 flex not-italic mt-6">
          <div className="mr-3 mt-0.5 text-agenda-blue-600 flex-[0_0_1rem]">
            <FaLocationDot />
          </div>
          <div>
            <div className="font-medium mb-0.5">{t("location")}</div>
            <div className="">
              {doctorAddress &&
                doctorAddress.map((addressItem: string, index: number) => {
                  return <div key={`address_${index}`}>{addressItem}</div>;
                })}
              <div>
                {doctorInfo?.docInf?.zip_code && (
                  <span>{doctorInfo?.docInf?.zip_code}&nbsp;</span>
                )}
                {doctorInfo?.docInf?.city && <span>{doctorInfo?.docInf?.city}</span>}
                {doctorInfo?.docInf?.nationality && (
                  <span>,&nbsp;{doctorInfo?.docInf?.nationality}</span>
                )}
              </div>
              <button
                onClick={()=>{scrollToElement('see_map')}}
                className="text-agenda-blue-600 underline text-sm border-none appearance-none shadow-none"
              >
                {t("see_map")}
              </button>
            </div>
          </div>
        </address>
        <div className="text-agenda-purple-800 flex not-italic mt-6">
          <div className="mr-3 text-agenda-blue-600 mt-1 flex-[0_0_1rem]">
            <FaCalendarDay />
          </div>
          <div>
            <div className="font-medium mb-0.5">{t("date")}</div>
            <div className="">
              {selectedDateTime ? (
                dayjs(selectedDateTime).format(MOMENTJS_DATE_FORMATES.cardDate)
              ) : (
                <div className="text-agenda-blue-200">________</div>
              )}
            </div>
          </div>
        </div>
        <div className="text-agenda-purple-800 flex not-italic font-light mt-6">
          <div className="mr-3 text-agenda-blue-600 mt-1 flex-[0_0_1rem]">
            <FaClock />
          </div>
          <div>
            <div className="font-medium mb-0.5">{t("time")}</div>
            <div className="">
              {selectedDateTime ? (
                dayjs(selectedDateTime).format(MOMENTJS_DATE_FORMATES.ampmtime)
              ) : (
                <div className="text-agenda-blue-200">________</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
